import posthog from "posthog-js";

const POSTHOG_KEY = "phc_soJ9JcFvHoFDmSDdEJGVWqEetv9jt47UoTyDfsvm2G29";
const POSTHOG_HOST = "https://us.i.posthog.com";

const APP_NAME = "inner-path";
const APP_ENV =
  typeof window === "undefined"
    ? "ssr"
    : window.location.hostname === "localhost"
      ? "development"
      : window.location.hostname.startsWith("id-preview--")
        ? "preview"
        : "production";

let initialized = false;

export function initPostHog() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    persistence: "localStorage+cookie",
  });

  posthog.register({ app: APP_NAME, app_name: APP_NAME, app_env: APP_ENV });
  posthog.people?.set?.({ app: APP_NAME, app_env: APP_ENV });

  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      const v = params.get(k);
      if (v) utm[k] = v;
    });
    if (Object.keys(utm).length > 0) {
      localStorage.setItem(
        "ip_utm",
        JSON.stringify({ ...utm, landed_at: new Date().toISOString(), referrer: document.referrer })
      );
      posthog.register(utm);
    }
  } catch {
    // ignore
  }
}

export function trackEvent(event: string, props?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture(`${APP_NAME}:${event}`, { app: APP_NAME, app_env: APP_ENV, ...props });
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.identify(`${APP_NAME}:${userId}`, { app: APP_NAME, app_env: APP_ENV, ...traits });
}

export function resetUser() {
  if (!initialized) return;
  posthog.reset();
}

/**
 * Fire an event only the first time per browser. Used for activation
 * milestones like `first_journal_entry_completed`. Privacy: never send
 * user content — only metadata flags.
 */
export function trackFirstTime(event: string, props?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const key = `ip_first:${event}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, new Date().toISOString());
  } catch {
    // proceed without dedupe if storage blocked
  }
  trackEvent(event, props);

  // Any first activation also implies first_value_completed (once).
  const FIRST_VALUE_EVENTS = new Set([
    "first_journal_entry_completed",
    "first_reflection_completed",
    "first_voice_entry_completed",
  ]);
  if (FIRST_VALUE_EVENTS.has(event)) {
    const fv = "ip_first:first_value_completed";
    try {
      if (!localStorage.getItem(fv)) {
        localStorage.setItem(fv, new Date().toISOString());
        trackEvent("first_value_completed", { via: event });
      }
    } catch {
      trackEvent("first_value_completed", { via: event });
    }
  }
}

/** Mark intent: e.g. user opened the composer for the first time. */
export function trackFirstIntent(event: string, props?: Record<string, unknown>) {
  trackFirstTime(event, props);
}

export { posthog };
